import { useQuery } from '@tanstack/react-query';
import { widgetsApi, conversationsApi, analyticsApi } from '../lib/api-client';
import { useAuth } from '../contexts/AuthContext';
import { Bot, MessageSquare, TrendingUp, Users } from 'lucide-react';

export default function Dashboard() {
  const { organization } = useAuth();

  const { data: widgetsData } = useQuery({
    queryKey: ['widgets', organization?.id],
    queryFn: () => widgetsApi.list(organization!.id),
    enabled: !!organization,
  });

  const { data: conversationsData } = useQuery({
    queryKey: ['conversations', organization?.id],
    queryFn: () => conversationsApi.list({ organizationId: organization!.id }),
    enabled: !!organization,
  });

  const { data: analyticsData } = useQuery({
    queryKey: ['analytics', organization?.id],
    queryFn: () => analyticsApi.getOrganizationAnalytics(organization!.id),
    enabled: !!organization,
  });

  const widgets = widgetsData?.data?.data || [];
  const conversations = conversationsData?.data?.data || [];
  const analytics = analyticsData?.data?.data || {};

  const stats = [
    {
      label: 'Active Widgets',
      value: widgets.filter((w: any) => w.status === 'active').length,
      icon: Bot,
      color: 'bg-blue-500',
    },
    {
      label: 'Total Conversations',
      value: conversations.length,
      icon: MessageSquare,
      color: 'bg-green-500',
    },
    {
      label: 'Messages Today',
      value: analytics.messagesToday || 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
    },
    {
      label: 'Unique Visitors',
      value: analytics.uniqueVisitors || 0,
      icon: Users,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-2">Overview of your AI chat platform</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.label} className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Widgets</h2>
          {widgets.length === 0 ? (
            <p className="text-gray-500">No widgets yet. Create your first widget to get started.</p>
          ) : (
            <ul className="space-y-3">
              {widgets.slice(0, 5).map((widget: any) => (
                <li key={widget.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{widget.name}</p>
                    <p className="text-sm text-gray-500">{widget.websiteUrl}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    widget.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {widget.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Conversations</h2>
          {conversations.length === 0 ? (
            <p className="text-gray-500">No conversations yet.</p>
          ) : (
            <ul className="space-y-3">
              {conversations.slice(0, 5).map((conv: any) => (
                <li key={conv.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">
                      {conv.visitorName || conv.visitorEmail || 'Anonymous'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(conv.startedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    conv.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {conv.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
